import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Card, Space, Spin, Switch, Tag, Typography } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import InfoAyuda, { AyudaBloque } from "../../../Components/InfoAyuda";
import { botAsistenteApi } from "../../../services/botAsistente.api";
import type { TwilioSettingsLectura } from "../../../interfaces/botAsistente.interfaces";
import { estiloTextoEtiqueta, formatoChile, tiempoRestante, vistaModoPrueba } from "./botAsistente.utils";
import { MOTIVO_SOLO_SUPERADMIN } from "./EstadoModoSeccion";
import { useAhora } from "./useAhora";

const { Text } = Typography;

type ClaveModo = "simuladorActivo" | "diagnosticoActivo";

interface DefinicionModo {
  clave: ClaveModo;
  titulo: string;
  horas: number;
  paraQueSirve: string;
  exito: { encender: string; apagar: string };
}

/**
 * La etiqueta de AntD no parte líneas (`white-space: nowrap`): «Encendido hasta el 31-10-2026 08:12 (hora de Chile) · quedan
 * 5 h 12 min» medía ~480 px y a 360 px de pantalla desbordaba TODA la página en horizontal. Esta la deja envolver.
 */
const ETIQUETA_QUE_ENVUELVE: React.CSSProperties = { margin: 0, whiteSpace: "normal", height: "auto", lineHeight: 1.5, padding: "2px 8px", wordBreak: "break-word" };

const MODOS: readonly DefinicionModo[] = [
  {
    clave: "simuladorActivo",
    titulo: "Simulador",
    horas: 24,
    paraQueSirve:
      "Sirve para probar el asistente sin consecuencias: lo que se pruebe se revisa igual que un gasto real, pero NUNCA se guarda, ni siquiera con el modo real encendido.",
    exito: { encender: "Simulador encendido por 24 horas", apagar: "Simulador apagado" },
  },
  {
    clave: "diagnosticoActivo",
    titulo: "Diagnóstico",
    horas: 48,
    paraQueSirve:
      "Sirve para que el equipo técnico consulte, solo para leer, un resumen de lo que hizo el asistente en las últimas horas: sin los textos de los mensajes ni los teléfonos completos.",
    exito: { encender: "Diagnóstico encendido por 48 horas", apagar: "Diagnóstico apagado" },
  },
];

export interface ModoPruebasSeccionProps {
  esSuperadmin: boolean;
  ajustes: TwilioSettingsLectura | null;
  cargando: boolean;
  error: boolean;
  onReintentar: () => void;
  /** Se llama después de cada intento de cambio, y cuando un modo vence con la pantalla abierta: vuelve a leer del servidor. */
  onCambioTerminado: () => void | Promise<void>;
}

/**
 * (c) Modo de pruebas: simulador (24 h) y diagnóstico (48 h). Los dos se apagan solos. Solo el superadministrador los
 * enciende; el resto ve en qué estado están, con la hora de vencimiento en hora chilena y cuánto falta.
 *
 * Los interruptores están controlados por lo que dice el servidor (igual que el modo real): un 403 muestra el motivo
 * y el interruptor queda como estaba. Si un modo vence mientras la pantalla sigue abierta, se muestra como vencido y se
 * vuelve a leer una vez.
 */
const ModoPruebasSeccion: React.FC<ModoPruebasSeccionProps> = ({
  esSuperadmin,
  ajustes,
  cargando,
  error,
  onReintentar,
  onCambioTerminado,
}) => {
  const ahora = useAhora();
  const [cambiando, setCambiando] = useState<ClaveModo | null>(null);
  const enCurso = useRef(false);
  /** Vencimientos ya avisados al contenedor, para pedir UNA sola lectura por vencimiento y no entrar en bucle. */
  const avisados = useRef<Set<string>>(new Set());

  // Cuando el reloj de ESTE dispositivo pasa el vencimiento de un modo que el servidor da por encendido, no se da nada por
  // vencido: se le vuelve a preguntar al servidor, UNA vez por vencimiento, y se muestra lo que él diga.
  useEffect(() => {
    if (!ajustes) return;
    for (const m of MODOS) {
      const hasta = m.clave === "simuladorActivo" ? ajustes.simuladorActivoHasta : ajustes.diagnosticoActivoHasta;
      const activo = m.clave === "simuladorActivo" ? ajustes.simuladorActivo : ajustes.diagnosticoActivo;
      if (!activo || !hasta) continue;
      const marca = `${m.clave}|${hasta}`;
      if (tiempoRestante(hasta, ahora).vencido && !avisados.current.has(marca)) {
        avisados.current.add(marca);
        void onCambioTerminado();
      }
    }
  }, [ahora, ajustes, onCambioTerminado]);

  const cambiar = async (m: DefinicionModo, encender: boolean) => {
    if (!esSuperadmin || enCurso.current) return;
    enCurso.current = true;
    setCambiando(m.clave);
    try {
      await botAsistenteApi.updateTwilioSettings(
        { [m.clave]: encender },
        { exito: encender ? m.exito.encender : m.exito.apagar, error: `No se pudo cambiar el ${m.titulo.toLowerCase()}` },
      );
    } finally {
      // Con éxito o con un 403, se vuelve a leer: el interruptor muestra lo que el servidor tiene.
      await onCambioTerminado();
      enCurso.current = false;
      setCambiando(null);
    }
  };

  return (
    <Card
      size="small"
      data-testid="asistente-seccion-pruebas"
      title={
        <Space size={4}>
          <span>Modo de pruebas</span>
          <InfoAyuda titulo="Modo de pruebas" testId="ayuda-pruebas">
            <AyudaBloque titulo="Simulador (24 horas)">
              Sirve para probar el asistente sin consecuencias. Se apaga solo a las 24 horas de haberlo encendido.
            </AyudaBloque>
            <AyudaBloque titulo="Diagnóstico (48 horas)">
              Permite al equipo técnico leer un resumen de la actividad del asistente para encontrar un problema. Es solo
              lectura y se apaga solo a las 48 horas. Tiene además una clave técnica que se configura aparte, en las
              variables de Railway.
            </AyudaBloque>
            <AyudaBloque titulo="¿Quién los enciende?">Solo el superadministrador.</AyudaBloque>
          </InfoAyuda>
        </Space>
      }
      extra={
        <Button size="small" icon={<ReloadOutlined />} onClick={() => onReintentar()} data-testid="pruebas-actualizar">
          Actualizar
        </Button>
      }
    >
      {cargando && ajustes === null && !error ? (
        <div style={{ textAlign: "center", padding: 24 }}>
          <Spin />
        </div>
      ) : error || ajustes === null ? (
        <Alert
          type="error"
          showIcon
          data-testid="pruebas-error"
          message="No se pudo leer el estado de los modos de prueba"
          action={
            <Button size="small" icon={<ReloadOutlined />} onClick={onReintentar}>
              Reintentar
            </Button>
          }
        />
      ) : (
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          {!esSuperadmin && (
            <Text type="secondary" data-testid="pruebas-solo-superadmin">
              {MOTIVO_SOLO_SUPERADMIN}
            </Text>
          )}
          {MODOS.map((m) => {
            const encendidoServidor = m.clave === "simuladorActivo" ? ajustes.simuladorActivo : ajustes.diagnosticoActivo;
            const hasta = m.clave === "simuladorActivo" ? ajustes.simuladorActivoHasta : ajustes.diagnosticoActivoHasta;
            // MANDA EL SERVIDOR: el interruptor y la etiqueta siguen su booleano. El reloj del dispositivo solo pone la
            // cuenta regresiva; nunca decide que algo «venció» (con el reloj adelantado se mostraba «Vencido» un modo vigente).
            const vista = vistaModoPrueba(encendidoServidor, hasta, ahora);
            return (
              <div key={m.clave} data-testid={`modo-${m.clave}`}>
                <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10 }}>
                  <Switch
                    checked={encendidoServidor}
                    disabled={!esSuperadmin || cambiando !== null}
                    loading={cambiando === m.clave}
                    onChange={(valor) => void cambiar(m, valor)}
                    aria-label={`${m.titulo} (${m.horas} horas)`}
                    data-testid={`interruptor-${m.clave}`}
                  />
                  <Text strong>
                    {m.titulo} ({m.horas} h)
                  </Text>
                  {vista.tipo === "encendido" || vista.tipo === "reloj_pasado" ? (
                    <Tag color="green" style={{ ...ETIQUETA_QUE_ENVUELVE, ...estiloTextoEtiqueta("green") }} data-testid={`estado-${m.clave}`}>
                      Encendido hasta el {formatoChile(hasta)} (hora de Chile)
                      {vista.tipo === "encendido" && vista.restante ? ` · vence en ~${vista.restante}` : ""}
                    </Tag>
                  ) : vista.tipo === "vencido" ? (
                    <Tag style={ETIQUETA_QUE_ENVUELVE} data-testid={`estado-${m.clave}`}>
                      Vencido (dejó de valer el {formatoChile(hasta)}, hora de Chile)
                    </Tag>
                  ) : (
                    <Tag style={{ margin: 0 }} data-testid={`estado-${m.clave}`}>
                      Apagado
                    </Tag>
                  )}
                </div>
                {vista.tipo === "reloj_pasado" && (
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginTop: 6 }}
                    data-testid={`reloj-${m.clave}`}
                    message="Según la hora de este dispositivo ya venció, pero el servidor lo sigue dando por encendido. Vale lo que dice el servidor; revisa la fecha y la hora de tu dispositivo."
                  />
                )}
                <Text type="secondary" style={{ display: "block", marginTop: 4, fontSize: 13 }}>
                  {m.paraQueSirve}
                </Text>
              </div>
            );
          })}
        </Space>
      )}
    </Card>
  );
};

export default ModoPruebasSeccion;
